import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Sparkles, Droplet } from "lucide-react";

const RingText = () => (
  <motion.svg viewBox="0 0 200 200" className="absolute -right-2 -top-4 h-28 w-28 sm:h-40 sm:w-40"
    animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }} aria-hidden="true">
    <defs><path id="ring" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
    <circle cx="100" cy="100" r="96" fill="#2C221E" />
    <text fill="#F4DED3" fontSize="12.5" letterSpacing="2.6" fontFamily="Manrope, sans-serif" fontWeight="600">
      <textPath href="#ring">MENSTRUAL · FOLLICULAR · OVULATORY · LUTEAL · </textPath>
    </text>
    <circle cx="100" cy="100" r="16" fill="#C86D51" />
  </motion.svg>
);

export const HeroVisual = ({ parallaxY }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative mx-auto w-full max-w-[460px] [perspective:1200px]" onMouseMove={move} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]"
        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
        <div className="relative aspect-[4/5.4] overflow-hidden rounded-t-[999px] rounded-b-[2rem] bg-[#F4DED3] shadow-[0_40px_80px_-30px_rgba(44,34,30,0.45)]">
          <motion.img src="/img/phone-v2.webp" alt="Lunelle cycle tracking app on a phone" style={{ y: parallaxY }}
            className="absolute inset-0 h-[115%] w-full object-cover object-[50%_60%]" data-testid="hero-product-image" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,transparent_40%,rgba(44,34,30,0.35)_100%)]" />
        </div>
        <RingText />
        <motion.div style={{ translateZ: 60 }} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="absolute -left-4 top-[42%] rounded-2xl border border-white/60 bg-[#FDFBF7]/85 p-3.5 backdrop-blur-xl sm:-left-12" data-testid="hero-badge-phase">
          <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9E4429]"><Droplet className="h-3.5 w-3.5" /> Day 14</span>
          <span className="mt-1 block font-serif text-xl text-[#2C221E]">Ovulatory · Inner Summer</span>
        </motion.div>
        <motion.div style={{ translateZ: 90 }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55, duration: 0.8 }}
          className="absolute -bottom-6 right-2 max-w-[230px] rounded-2xl bg-[#2C221E] p-4 text-[#FBF5EE] sm:-right-10" data-testid="hero-badge-verse">
          <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E8B99A]"><Sparkles className="h-3.5 w-3.5" /> Today's verse</span>
          <span className="mt-2 block font-serif text-lg italic leading-snug">"I am fearfully and wonderfully made."</span>
          <span className="mt-1 block text-xs text-[#d9c7bd]">Psalm 139:14</span>
        </motion.div>
      </motion.div>
    </div>
  );
};
